class ScratchRevealCard extends HTMLElement {
  static REVEAL_THRESHOLD = 0.55;
  static SAMPLE_STRIDE_BYTES = 14 * 4;
  static BRUSH_RADIUS = 18;

  connectedCallback() {
    if (this._built) return;
    this._built = true;
    this._initialized = false;
    this._revealed = false;
    this._scratching = false;
    this._lastPoint = null;

    this.classList.add('scratch-reveal-card');

    this._revealLayer = document.createElement('div');
    this._revealLayer.className = 'scratch-reveal-card__reveal';
    this._revealLayer.setAttribute('aria-live', 'polite');

    this._canvas = document.createElement('canvas');
    this._canvas.className = 'scratch-reveal-card__canvas';
    this._canvas.setAttribute('tabindex', '0');

    this.append(this._revealLayer, this._canvas);
  }

  disconnectedCallback() {
    this._teardownListeners();
    if (this._resizeObserver) this._resizeObserver.disconnect();
  }

  // Called by the parent popup once the code is known and this card becomes visible.
  // Guarded so re-entering the visible state doesn't re-run setup or double-bind listeners.
  init(code) {
    if (this._initialized) return;
    this._initialized = true;
    this._code = code;

    this._canvas.setAttribute('aria-label', this.dataset.instructions || 'Scratch card');
    this._renderRevealLayer();

    this._ctx = this._canvas.getContext('2d');
    this._setupCanvas();
    this._bindEvents();

    this._resizeObserver = new ResizeObserver(() => this._handleResize());
    this._resizeObserver.observe(this);
  }

  _renderRevealLayer() {
    const copyLabel = this.dataset.copyText || 'Copy';
    this._revealLayer.innerHTML = `
      <span class="scratch-reveal-card__text">
        <span class="scratch-reveal-card__label">${this._escapeHtml(this.dataset.labelText || 'Your promo code')}</span>
        <span class="scratch-reveal-card__code">${this._escapeHtml(this._code)}</span>
      </span>
      <button type="button" class="scratch-reveal-card__copy">${this._escapeHtml(copyLabel)}</button>
    `;
    this._copyButton = this._revealLayer.querySelector('.scratch-reveal-card__copy');
    this._copyButton.addEventListener('click', () => this._copyCode());
  }

  async _copyCode() {
    const copyLabel = this.dataset.copyText || 'Copy';
    const copiedLabel = this.dataset.copiedText || 'Copied!';
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(this._code);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = this._code;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
      }
      this._copyButton.textContent = copiedLabel;
      clearTimeout(this._copyResetTimeout);
      this._copyResetTimeout = setTimeout(() => {
        this._copyButton.textContent = copyLabel;
      }, 2000);
    } catch (err) {
      console.error('[scratch-reveal-card] copy failed', err);
    }
  }

  _escapeHtml(value) {
    const div = document.createElement('div');
    div.textContent = value ?? '';
    return div.innerHTML;
  }

  _setupCanvas() {
    const rect = this.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    this._width = rect.width;
    this._height = rect.height;
    this._canvas.width = Math.max(1, Math.round(rect.width * dpr));
    this._canvas.height = Math.max(1, Math.round(rect.height * dpr));
    this._canvas.style.width = `${rect.width}px`;
    this._canvas.style.height = `${rect.height}px`;
    this._ctx.setTransform(1, 0, 0, 1, 0, 0);
    this._ctx.scale(dpr, dpr);
    this._drawOverlay();
  }

  _drawOverlay() {
    const ctx = this._ctx;
    const width = this._width;
    const height = this._height;

    ctx.globalCompositeOperation = 'source-over';
    ctx.clearRect(0, 0, width, height);

    // Dark diagonal stripes — matches the scratch card's dark surface, not the brand yellow.
    const tileSize = 20;
    const tile = document.createElement('canvas');
    tile.width = tileSize;
    tile.height = tileSize;
    const tileCtx = tile.getContext('2d');
    tileCtx.fillStyle = '#2e2e2e';
    tileCtx.fillRect(0, 0, tileSize, tileSize);
    tileCtx.fillStyle = '#333333';
    tileCtx.save();
    tileCtx.translate(tileSize / 2, tileSize / 2);
    tileCtx.rotate((45 * Math.PI) / 180);
    tileCtx.fillRect(-tileSize, -tileSize / 4, tileSize * 2, tileSize / 2);
    tileCtx.restore();

    const pattern = ctx.createPattern(tile, 'repeat');
    ctx.fillStyle = pattern;
    ctx.fillRect(0, 0, width, height);

    // Erase-only from here on — all scratch strokes subtract from this overlay.
    ctx.globalCompositeOperation = 'destination-out';
  }

  _bindEvents() {
    this._onMouseDown = (event) => this._startScratch(event.clientX, event.clientY);
    this._onMouseMove = (event) => {
      if (this._scratching) this._continueScratch(event.clientX, event.clientY);
    };
    this._onMouseUp = () => this._endScratch();

    this._onTouchStart = (event) => {
      event.preventDefault();
      const touch = event.touches[0];
      if (touch) this._startScratch(touch.clientX, touch.clientY);
    };
    this._onTouchMove = (event) => {
      if (!this._scratching) return;
      event.preventDefault();
      const touch = event.touches[0];
      if (touch) this._continueScratch(touch.clientX, touch.clientY);
    };
    this._onTouchEnd = () => this._endScratch();

    this._onKeydown = (event) => {
      if (event.code === 'Enter' || event.code === 'Space') {
        event.preventDefault();
        this._reveal();
      }
    };

    this._canvas.addEventListener('mousedown', this._onMouseDown);
    window.addEventListener('mousemove', this._onMouseMove);
    window.addEventListener('mouseup', this._onMouseUp);

    this._canvas.addEventListener('touchstart', this._onTouchStart, { passive: false });
    this._canvas.addEventListener('touchmove', this._onTouchMove, { passive: false });
    this._canvas.addEventListener('touchend', this._onTouchEnd);

    this._canvas.addEventListener('keydown', this._onKeydown);
  }

  _teardownListeners() {
    if (!this._onMouseDown) return;
    this._canvas.removeEventListener('mousedown', this._onMouseDown);
    window.removeEventListener('mousemove', this._onMouseMove);
    window.removeEventListener('mouseup', this._onMouseUp);
    this._canvas.removeEventListener('touchstart', this._onTouchStart);
    this._canvas.removeEventListener('touchmove', this._onTouchMove);
    this._canvas.removeEventListener('touchend', this._onTouchEnd);
    this._canvas.removeEventListener('keydown', this._onKeydown);
  }

  _handleResize() {
    if (this._revealed || !this._initialized) return;
    this._setupCanvas();
  }

  _startScratch(clientX, clientY) {
    if (this._revealed) return;
    this._scratching = true;
    const point = this._toLocalPoint(clientX, clientY);
    this._lastPoint = point;
    this._drawDab(point.x, point.y);
    this._scheduleThresholdCheck();
  }

  _continueScratch(clientX, clientY) {
    if (this._revealed) return;
    const point = this._toLocalPoint(clientX, clientY);
    this._drawLine(this._lastPoint || point, point);
    this._lastPoint = point;
    this._scheduleThresholdCheck();
  }

  _endScratch() {
    this._scratching = false;
    this._lastPoint = null;
  }

  _toLocalPoint(clientX, clientY) {
    const rect = this._canvas.getBoundingClientRect();
    return { x: clientX - rect.left, y: clientY - rect.top };
  }

  _drawDab(x, y) {
    const ctx = this._ctx;
    ctx.beginPath();
    ctx.arc(x, y, ScratchRevealCard.BRUSH_RADIUS, 0, Math.PI * 2);
    ctx.fill();
  }

  _drawLine(from, to) {
    const dist = Math.hypot(to.x - from.x, to.y - from.y);
    const step = ScratchRevealCard.BRUSH_RADIUS / 2;
    const steps = Math.max(1, Math.ceil(dist / step));
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      this._drawDab(from.x + (to.x - from.x) * t, from.y + (to.y - from.y) * t);
    }
  }

  _scheduleThresholdCheck() {
    if (this._revealed || this._checkQueued) return;
    this._checkQueued = true;
    requestAnimationFrame(() => {
      this._checkQueued = false;
      this._checkThreshold();
    });
  }

  _checkThreshold() {
    if (this._revealed) return;
    const { width, height } = this._canvas;
    if (!width || !height) return;

    let data;
    try {
      data = this._ctx.getImageData(0, 0, width, height).data;
    } catch (err) {
      return;
    }

    let sampled = 0;
    let erased = 0;
    for (let i = 3; i < data.length; i += ScratchRevealCard.SAMPLE_STRIDE_BYTES) {
      sampled++;
      if (data[i] < 64) erased++;
    }

    if (sampled > 0 && erased / sampled >= ScratchRevealCard.REVEAL_THRESHOLD) {
      this._reveal();
    }
  }

  _reveal() {
    if (this._revealed) return;
    this._revealed = true;
    this._scratching = false;
    this._canvas.classList.add('is-revealed');
    this._teardownListeners();
    this.dispatchEvent(new CustomEvent('scratch-revealed', { bubbles: true, detail: { code: this._code } }));
  }
}

customElements.define('scratch-reveal-card', ScratchRevealCard);
